import { useLazyQuery } from '@apollo/client/react'
import { GET_CHANNEL_AUTHORIZATION_INFO } from '../queries'
import {
  getRedirectUri,
  handleSettingCookiesForConnection,
  setChannelCodeVerifierCookie,
  setChannelStateCookies,
  setRedirectURLCookie,
} from '../../utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { InstagramAuthMethod, Service } from '../../types'
import {
  ChannelAuthorizationInstagramApi,
  Service as GqlService,
} from '../../../../../gql/graphql'

import type {
  GetAuthorizationInfoArgs,
  UseAuthorizationInfoReturn,
} from './types'
import { useDeprecateAccountServiceTreatment } from './useDeprecateAccountServiceTreatment'
import { useDispatch, useSelector } from 'react-redux'
import type { OrchestratorRootState } from '../../../../../common/events/types'
import trackChannelCTAClicked from '../../../../../tracking/trackChannelCTAClicked'
import { useUser } from '../../../../../common/context/User'
import { getChannelFromAccount } from '../../../../../common/utils/channels'
import { setAuthRedirectUrl } from '../../../store/channelConnectionsReducer'
import { getCookie } from '../../../../../common/utils/cookies'

// The local `Service` enum carries `instagram_login_api`, an app-shell-only
// auth-method variant that the GraphQL `Service` enum does not model. Map it to
// `instagram` and pass through the rest, so the authorization query receives the
// schema's `Service` enum without a cast.
const SERVICE_TO_GQL_SERVICE: Record<
  Exclude<Service, Service.whatsapp>,
  GqlService
> = {
  [Service.facebook]: GqlService.Facebook,
  [Service.instagram]: GqlService.Instagram,
  [Service.instagram_login_api]: GqlService.Instagram,
  [Service.linkedin]: GqlService.Linkedin,
  [Service.pinterest]: GqlService.Pinterest,
  [Service.twitter]: GqlService.Twitter,
  [Service.tiktok]: GqlService.Tiktok,
  [Service.googlebusiness]: GqlService.Googlebusiness,
  [Service.mastodon]: GqlService.Mastodon,
  [Service.youtube]: GqlService.Youtube,
  [Service.threads]: GqlService.Threads,
  [Service.bluesky]: GqlService.Bluesky,
  [Service.substack]: GqlService.Substack,
}

export default function useAuthorizationInfo(): UseAuthorizationInfoReturn {
  const user = useUser()
  const dispatch = useDispatch()

  // Track request ID to prevent race conditions where stale query results
  // from a previous channel connection attempt overwrite the current one
  const requestIdRef = useRef(0)

  // Get destinationUrl and cta from store if it has been set from connectChannel action
  const {
    destinationUrl,
    cta,
    selectedService,
    skipCelebrationModal,
    prefillDataAfterConnection,
  } = useSelector((state: OrchestratorRootState) => state.channelConnections)

  // A call made before the Split treatment settled, replayed once it does.
  // The state mirrors the ref for the loading flag.
  const pendingArgsRef = useRef<GetAuthorizationInfoArgs | null>(null)
  const [hasPendingRequest, setHasPendingRequest] = useState(false)

  // Invalidate in-flight requests when connection is reset or hook unmounts
  useEffect(() => {
    if (!selectedService) {
      requestIdRef.current++
      pendingArgsRef.current = null
      setHasPendingRequest(false)
    }
    return () => {
      requestIdRef.current++
    }
  }, [selectedService])

  // Evaluated with `selectedService` from Redux rather than `args.service`
  // because hooks cannot be called inside callbacks. This is safe because
  // selectedService is always set before the user can trigger
  // handleGetAuthorizationInfo — the UI flow requires selecting a service
  // first, which updates Redux and triggers a re-render with the correct value.
  //
  // The treatment must be settled before reading it: until the Split client
  // has loaded, the flag reads as the default ('off'), which resolves to the
  // legacy account.buffer.com redirect URI. Several connect flows fire
  // `handleGetAuthorizationInfo` on mount, so reading the flag eagerly would
  // send organizations the flag is on for through account.buffer.com. The hook
  // waits for the Split SDK to settle (SDK_READY, or SDK_READY_TIMED_OUT which
  // falls back to 'off') rather than trusting `useSplitEnabled`'s `isReady`,
  // which is `true` while the client is still `null`. @see CORE-6102, CORE-7824
  const { isResolved: isSplitReady, useCallbacksService } =
    useDeprecateAccountServiceTreatment(selectedService ?? '')

  // We avoid useLazyQuery's onCompleted callback because it captures stale
  // closure values, causing a race condition where a slow response from a
  // previous connection attempt overwrites the current authRedirectUrl (CHAN-8740).
  // Using async/await on the execute function + requestIdRef lets us correlate
  // each response to its specific request and discard stale results.
  const [getAuthorizationInfo, { loading, data, error }] = useLazyQuery(
    GET_CHANNEL_AUTHORIZATION_INFO,
  )

  const handleGetAuthorizationInfo = useCallback(
    async (args: GetAuthorizationInfoArgs) => {
      if (args.service === Service.whatsapp) {
        requestIdRef.current++
        return
      }

      // Wait for the Split SDK before requesting authorization info: the
      // redirect URI depends on the `deprecate-account-service` treatment, and
      // requesting it while the SDK is unresolved would lock in the legacy
      // account.buffer.com URI. Callers that fire on mount re-run this once the
      // treatment settles, because `isSplitReady` is a dependency below and
      // flips the callback's identity. Callers that fire from a user action
      // (e.g. Mastodon's Continue) do not re-run, so the call is queued and
      // replayed below. @see CORE-6102, CORE-7824
      if (!isSplitReady) {
        pendingArgsRef.current = args
        setHasPendingRequest(true)
        return
      }
      pendingArgsRef.current = null
      setHasPendingRequest(false)

      const redirectUri = getRedirectUri(
        args.service,
        args.authMethod,
        useCallbacksService,
      )
      // The redirect URI cookie used by the post-OAuth page (in Publish) is
      // NOT written here: multiple flows can prefetch authorization info on
      // the same screen, and the last write would clobber the others. It is
      // written at redirect time from the chosen authorization URL instead
      // (see setOAuthRedirectUriCookieFromAuthUrl).
      const channelToRefresh = getChannelFromAccount(
        user,
        args?.selectedRefreshChannelId || '',
      )
      const serverFromCookie = getCookie<string>({
        key: 'MastodonServer',
        prefix: 'oauthChannels',
      })

      const channelServer =
        args?.server || channelToRefresh?.serverUrl || serverFromCookie
      handleSettingCookiesForConnection({
        followBuffer: args?.followBuffer,
        server: channelServer,
        selectedRefreshChannelId: args.selectedRefreshChannelId,
      })

      const currentRequestId = ++requestIdRef.current

      // The lazy-query execute promise rejects on error (network/GraphQL) and on
      // abort (the hook unmounts or a newer request supersedes this one). The
      // failure surfaces reactively via the `error` state below, so swallow the
      // rejection here to avoid an unhandled promise rejection and stop
      // processing this stale/failed request.
      let result: Awaited<ReturnType<typeof getAuthorizationInfo>>

      try {
        result = await getAuthorizationInfo({
          variables: {
            input: {
              service: SERVICE_TO_GQL_SERVICE[args.service],
              redirectUri,
              metadata: {
                // `server` is non-nullable in the schema, so the metadata is
                // omitted entirely when no server could be resolved rather than
                // sending an empty value.
                mastodonMetadata:
                  args.service === Service.mastodon && channelServer
                    ? {
                        server: channelServer,
                      }
                    : undefined,
                instagramMetadata:
                  args.service === Service.instagram ||
                  args.service === Service.instagram_login_api
                    ? {
                        apiVersion:
                          args.service === Service.instagram_login_api ||
                          args.authMethod === InstagramAuthMethod.instagramLogin
                            ? ChannelAuthorizationInstagramApi.InstagramLogin
                            : ChannelAuthorizationInstagramApi.FacebookLogin,
                      }
                    : undefined,
              },
            },
          },
        })
      } catch {
        return
      }

      // Discard stale results if a newer request was made or state was reset
      if (currentRequestId !== requestIdRef.current) return

      // Apply side effects: set OAuth cookies and dispatch redirect URL to Redux
      const authInfo = result.data?.channelAuthorizationInfo

      if (authInfo?.state) {
        setChannelStateCookies(args.service, authInfo.state)
      }
      // PKCE services (Substack) return the code verifier next to the state;
      // the post-OAuth page sends it back with the token exchange.
      if (authInfo?.codeVerifier) {
        setChannelCodeVerifierCookie(args.service, authInfo.codeVerifier)
      }

      const url = new URL(destinationUrl || window.location.href)

      if (skipCelebrationModal) {
        url.searchParams.set('skipCelebrationModal', 'true')
      }
      if (prefillDataAfterConnection) {
        url.searchParams.set('prefillDraftData', 'true')
      }
      setRedirectURLCookie(url.toString())

      dispatch(
        setAuthRedirectUrl({
          url: authInfo?.url,
        }),
      )

      trackChannelCTAClicked({ payload: { service: args.service, cta }, user })
    },
    [
      getAuthorizationInfo,
      cta,
      user,
      dispatch,
      destinationUrl,
      skipCelebrationModal,
      prefillDataAfterConnection,
      useCallbacksService,
      isSplitReady,
    ],
  )

  // Replay a call queued while the treatment was unsettled. On-mount
  // prefetchers also re-run on the callback's new identity; their identical
  // query is deduplicated by Apollo and the older result is discarded by
  // `requestIdRef`.
  useEffect(() => {
    const pendingArgs = pendingArgsRef.current
    if (!isSplitReady || !pendingArgs) return
    void handleGetAuthorizationInfo(pendingArgs)
  }, [isSplitReady, handleGetAuthorizationInfo])

  const channelAuthorizationInfo =
    selectedService === Service.whatsapp
      ? undefined
      : data?.channelAuthorizationInfo

  return {
    url: channelAuthorizationInfo?.url,
    state: channelAuthorizationInfo?.state,
    redirectUri: channelAuthorizationInfo?.redirectUri,
    handleGetAuthorizationInfo,
    // A call waiting on the Split treatment counts as loading, so submit
    // buttons stay disabled until it goes out.
    isAuthorizationInfoLoading: loading || hasPendingRequest,
    // `ChannelAuthorizationInfoResponse` has no error branch in the schema, so
    // failures (e.g. an unreachable Mastodon server) surface as GraphQL errors
    // on the query itself.
    authorizationInfoErrorMessage: error?.message,
  }
}
